import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import '../styles/Layout.css';
import { CART_UPDATED_EVENT, getCart, getCartCount } from '../utils/cart';
import hereticLogo from '../assets/images/heretic.svg';
import whatsappIcon from '../assets/images/whatsapp.svg';
import blockImage1 from '../assets/portfolio/future_1.webp';
import blockImage2 from '../assets/portfolio/Matteo_1.webp';
import blockImage3 from '../assets/portfolio/fizzyjuice/FizzyJuice2.webp';
import blockImage4 from '../assets/portfolio/llama/llama_block_5.webp';
import footerBg from '../assets/footerbg.svg';

const TAGLINES_BY_DAY = {
  Sunday: 'Heretic Studio',
  Monday: 'Heretic Studio',
  Tuesday: 'Heretic Studio',
  Wednesday: 'Heretic Studio',
  Thursday: 'Heretic Studio',
  Friday: 'Heretic Studio',
  Saturday: 'Heretic Studio',
};

const TypewriterText = ({ text }) => {
  const [displayed, setDisplayed] = useState('');

  useEffect(() => {
    setDisplayed('');
    if (!text) return;

    let index = 0;
    const interval = setInterval(() => {
      index += 1;
      setDisplayed(text.slice(0, index));
      if (index >= text.length) {
        clearInterval(interval);
      }
    }, 40);

    return () => {
      clearInterval(interval);
    };
  }, [text]);

  return <>{displayed}</>;
};

const Layout = () => {
  const location = useLocation();
  const todayName = new Date().toLocaleDateString(undefined, { weekday: 'long' });
  const studioTagline = TAGLINES_BY_DAY[todayName] || 'Concept design studio';
  const [cartCount, setCartCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isHeroInView, setIsHeroInView] = useState(true);
  const isContactPage = location.pathname === '/contact';
  const isHomePage = location.pathname === '/';
  const isStudioPage = location.pathname === '/studio';
  const isProjectsPage = location.pathname === '/work';
  const footerClassName = location.pathname === '/new-project'
    ? 'footer_newproject'
    : isContactPage
      ? 'contact-footer'
      : '';

  useEffect(() => {
    // Scroll to top when route changes
    setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (!isHomePage && !isStudioPage) {
      setIsHeroInView(false);
      return undefined;
    }

    const heroSelector = isHomePage ? '.home-parallax' : '.studio-hero';
    const heroEl = document.querySelector(heroSelector);

    if (!heroEl) {
      setIsHeroInView(false);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHeroInView(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(heroEl);

    return () => {
      observer.disconnect();
    };
  }, [isHomePage, isStudioPage]);

  const navClassName = [
    'nav',
    isHomePage ? 'nav--home' : '',
    isStudioPage ? 'nav--studio' : '',
    isProjectsPage ? 'nav--projects' : '',
    (isHomePage || isStudioPage) && isHeroInView ? 'nav--hero-top' : '',
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    const update = () => {
      const cart = getCart();
      setCartCount(getCartCount(cart));
    };

    update();

    const onCartUpdated = () => update();
    const onStorage = (e) => {
      if (e && e.key && e.key !== 'heretic_cart') return;
      update();
    };

    window.addEventListener(CART_UPDATED_EVENT, onCartUpdated);
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener(CART_UPDATED_EVENT, onCartUpdated);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return (
    <div className="app">
      <nav className={navClassName}>
        <Link
          to="/"
          className="studio-tagline"
        >
          <span>
            <TypewriterText text={studioTagline} />
          </span>
        </Link>
        
        {/* Desktop Navigation */}
        <ul className="nav-links nav-links--center">
          <li><Link to="/studio" className="nav1">Studio</Link></li>
          <li><Link to="/work" className="nav2">Projects</Link></li>
          <li><Link to="/partners" className="nav4">Partners</Link></li>
          {/* <li><Link to="/cult" className="nav4">Cult</Link></li> */}
          {/* <li><Link to="/shop" className="nav3">Shop</Link></li> */}

          {/* <li><Link to="/accelerator" className="nav3">Accelerator</Link></li> */}
        </ul>
        
        <div className="nav-contact">
          {/* {cartCount > 0 ? (
            <Link to="/cart" className="nav4">Cart ({cartCount})</Link>
          ) : null} */}
          <Link to="/contact" className="nav1">Contact</Link>
        </div>
        
        {/* Mobile Menu Button */}
        <button 
          className={`mobile-menu-button ${mobileMenuOpen ? 'mobile-menu-button--open' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
        >
        </button>
      </nav>
      
      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}>
        <button
          className="mobile-menu-close"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Close mobile menu"
        >
          ×
        </button>
        <div className="mobile-menu-content">
          <ul className="mobile-nav-links">
            <li><Link to="/" onClick={() => setMobileMenuOpen(false)}>Home</Link></li>
            <li><Link to="/studio" onClick={() => setMobileMenuOpen(false)}>Studio</Link></li>
            <li><Link to="/work" onClick={() => setMobileMenuOpen(false)}>Projects</Link></li>
            <li><Link to="/partners" onClick={() => setMobileMenuOpen(false)}>Partners</Link></li>
            {/* <li><Link to="/shop" onClick={() => setMobileMenuOpen(false)}>Shop</Link></li> */}
            {/* {cartCount > 0 ? (
              <li><Link to="/cart" onClick={() => setMobileMenuOpen(false)}>Cart ({cartCount})</Link></li>
            ) : null} */}
            <li><Link to="/contact" onClick={() => setMobileMenuOpen(false)}>Contact</Link></li>
          </ul>

          <p className="mobile-menu-description">
            An independent concept design studio specialising in product and UX design, helping people turn bold
            ideas into intuitive, user-focused digital experiences.
          </p>

          <div className="mobile-menu-contact">
            <a
              href="mailto:info@heretic-studio.co.uk"
              className="mobile-menu-email"
            >
              info@heretic-studio.co.uk
            </a>

            <a
              href="https://wa.me/447941222437"
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-button"
            >
              <img src={whatsappIcon} alt="WhatsApp" />
            </a>
          </div>
        </div>
      </div>
      <main className={isContactPage ? 'contact-page' : undefined}>
        <Outlet />
      </main>
      <footer className={`site-footer ${footerClassName}`}>
        <div className="site-footer__bg">
          <img src={footerBg} alt="" aria-hidden="true" />
        </div>
        <div className="site-footer__inner">
          <div className="site-footer__hero">
            <h2 className="site-footer__title">The good kind of trouble</h2>
            <div className="site-footer__aside">
              <p className="site-footer__description">
                Design thinking at the heart of your business — not as a coat of paint at the end, but as the engine from the start.
              </p>
              <Link to="/contact" className="site-footer__button">Let&apos;s talk</Link>
            </div>
          </div>
          <div className="site-footer__blocks">
            <div className="site-footer__block site-footer__block--tall">
              <img src={blockImage1} alt="Portfolio highlight" loading="lazy" />
            </div>
            <div className="site-footer__block">
              <img src={blockImage2} alt="Portfolio highlight" loading="lazy" />
            </div>
            <div className="site-footer__block site-footer__block--short">
              <img src={blockImage3} alt="Portfolio highlight" loading="lazy" />
            </div>
            <div className="site-footer__block site-footer__block--tall">
              <img src={blockImage4} alt="Portfolio highlight" loading="lazy" />
            </div>
          </div>
          <div className="site-footer__meta">
            <span>Heretic Studio {new Date().getFullYear()}</span>
            <a href="mailto:info@heretic-studio.co.uk">info@heretic-studio.co.uk</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
